import { useState, useEffect } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Users,
  User,
  Layout,
  Key,
  Eye,
  EyeOff,
  Copy,
  Calendar,
  Ban,
  CheckCircle,
  Send,
  Edit,
  Sparkles,
  MapPin,
  Globe,
  Phone,
  Mail,
  MessageSquare,
  FileText,
  BarChart3,
  CreditCard,
  Clock,
  Download,
  ExternalLink,
  RefreshCw,
  LogOut,
  Trash2,
  AlertTriangle,
  XCircle,
  Palette,
  Monitor,
  Lock,
  ChevronRight,
  Hash
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function CompanyDetailsPage() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { isSuperAdmin } = useAuth();
  
  // Get context from BackofficeLayout - always call the hook
  const outletContext = useOutletContext() || {};
  const { permissions: backofficePermissions } = outletContext;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [viewMode, setViewMode] = useState("readonly");
  
  // Edit Company Details - Inline editing
  const [editingCompany, setEditingCompany] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    billing_email: "",
    phone: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    country: "",
    vat_number: "",
    vat_registered: false
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Session Management
  const [userSessions, setUserSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(false);

  // Cancel Subscription
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelType, setCancelType] = useState(null); // 'subscription' or 'white_label'
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [processingCancel, setProcessingCancel] = useState(false);

  // Layout Management
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [showDeleteLayoutDialog, setShowDeleteLayoutDialog] = useState(false);
  const [deletingLayout, setDeletingLayout] = useState(false);

  // Customer Tickets
  const [customerTickets, setCustomerTickets] = useState([]);
  const [paymentAudit, setPaymentAudit] = useState([]);
  const [downloadingEvidence, setDownloadingEvidence] = useState(false);
  // Lockout status for the owner — populated by /auth/admin/lockout-status
  // so we can show an "Unlock login" button only when the user is
  // actually locked out. Refreshes whenever the profile loads.
  const [lockoutStatus, setLockoutStatus] = useState(null);
  const [unlockingLogin, setUnlockingLogin] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [ticketThread, setTicketThread] = useState([]);
  const [ticketReply, setTicketReply] = useState("");
  const [loadingThread, setLoadingThread] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedTicketData, setExpandedTicketData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [updatingTicketStatus, setUpdatingTicketStatus] = useState(false);

  // Change Plan Dialog
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [availablePlans, setAvailablePlans] = useState({});
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [changingPlan, setChangingPlan] = useState(false);

  useEffect(() => {
    // Allow access for both super admins and backoffice users with customer permission
    const hasCustomerPermission = isSuperAdmin || backofficePermissions?.includes('customers');
    if (!hasCustomerPermission && !isSuperAdmin) {
      // Try fetching anyway - the backend will check permissions
      fetchData();
    } else {
      fetchData();
    }
  }, [isSuperAdmin, companyId, backofficePermissions]);
const fetchData = async () => {
    setLoading(true);
    try {
      // Try backoffice endpoint first (works for all backoffice users with permission)
      let response;
      try {
        response = await axios.get(`${API}/backoffice/customer/${companyId}`, { withCredentials: true });
        // Set edit permissions from response
        setCanEdit(response.data.can_edit || false);
        setViewMode(response.data.view_mode || "readonly");
      } catch (backofficeError) {
        // Fallback to super-admin endpoint if backoffice endpoint fails
        if (isSuperAdmin) {
          response = await axios.get(`${API}/super-admin/company/${companyId}/full`, { withCredentials: true });
          setCanEdit(true);
          setViewMode("full");
        } else {
          throw backofficeError;
        }
      }
      
      setData(response.data);
      // Initialize edit form with current data
      const company = response.data.company;
      setEditForm({
        name: company?.name || "",
        billing_email: company?.billing_email || "",
        phone: company?.phone || "",
        address: company?.address || ""
      });
      
      // Fetch customer tickets
      try {
        const ticketsRes = await axios.get(`${API}/backoffice/customer/${companyId}/tickets`, { withCredentials: true });
        setCustomerTickets(ticketsRes.data.tickets || []);
      } catch { setCustomerTickets([]); }

      // Fetch payment audit trail (chargeback-defence log) — best-effort
      try {
        const auditRes = await axios.get(
          `${API}/subscriptions/super-admin/companies/${companyId}/payment-audit?limit=200`,
          { withCredentials: true }
        );
        setPaymentAudit(auditRes.data?.rows || []);
      } catch { setPaymentAudit([]); }

      // Fetch lockout status for the company owner's email so we can show
      // the "Unlock login" button when they're currently rate-limited.
      const ownerEmail = response.data?.company?.owner_email || response.data?.company?.email;
      if (ownerEmail) {
        try {
          const lockRes = await axios.get(
            `${API}/auth/admin/lockout-status?email=${encodeURIComponent(ownerEmail)}`,
            { withCredentials: true }
          );
          setLockoutStatus({ ...lockRes.data, email: ownerEmail });
        } catch { setLockoutStatus(null); }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view this customer");
      } else {
        toast.error("Failed to load company details");
      }
      navigate('/backoffice/customers');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const openChangePlanDialog = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/plans`, { withCredentials: true });
      setAvailablePlans(res.data.plans || {});
    } catch (e) {
      console.error("Failed to fetch plans:", e);
    }
    setSelectedPlanId("");
    setShowChangePlanDialog(true);
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId) { toast.error("Please select a plan"); return; }
    const subscription = data?.subscription;
    const confirmMsg = `Change plan from "${subscription?.plan_id}" to "${selectedPlanId}"?`;
    if (!window.confirm(confirmMsg)) return;
    setChangingPlan(true);
    try {
      await axios.put(`${API}/backoffice/customer/${company.company_id}/change-plan`, { new_plan_id: selectedPlanId }, { withCredentials: true });
      toast.success(`Plan changed to ${selectedPlanId}`);
      setShowChangePlanDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadApiKey = () => {
    if (!data?.company?.api_key) return;
    const blob = new Blob([`SEATPILOT_API_KEY=${data.company.api_key}\nCOMPANY_ID=${data.company.company_id}`], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seatpilot-api-key-${data.company.name.toLowerCase().replace(/\s+/g, '-')}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('API key downloaded');
  };

  const handleSendPasswordReset = async (user) => {
    try {
      await axios.post(`${API}/super-admin/user/send-password-reset`, {
        user_id: user.user_id
      }, { withCredentials: true });
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    }
  };

  const handleBlockUser = async (user, block) => {
    const action = block ? "block" : "unblock";
    if (!window.confirm(`Are you sure you want to ${action} ${user.email}?`)) return;
    
    try {
      await axios.post(`${API}/super-admin/user/${action}`, {
        user_id: user.user_id
      }, { withCredentials: true });
      toast.success(`User ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  // Fetch ticket thread for inline view
  const fetchTicketThread = async (ticketId) => {
    if (expandedTicketId === ticketId) {
      setExpandedTicketId(null);
      setTicketThread([]);
      setExpandedTicketData(null);
      return;
    }
    setExpandedTicketId(ticketId);
    setLoadingThread(true);
    setTicketReply("");
    try {
      const [threadRes, staffRes] = await Promise.all([
        axios.get(`${API}/backoffice/customer/${companyId}/tickets/${ticketId}/thread`, { withCredentials: true }),
        staffList.length === 0 
          ? axios.get(`${API}/backoffice/staff/list`, { withCredentials: true }).catch(() => ({ data: { staff: [] } }))
          : Promise.resolve(null)
      ]);
      setTicketThread(threadRes.data.messages || []);
      setExpandedTicketData(threadRes.data.ticket || null);
      if (staffRes) setStaffList(staffRes.data.staff || []);
    } catch (err) {
      toast.error("Failed to load ticket thread");
      setTicketThread([]);
    } finally {
      setLoadingThread(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    setUpdatingTicketStatus(true);
    try {
      await axios.put(`${API}/backoffice/customer/${companyId}/tickets/${ticketId}/status`, {
        status: newStatus
      }, { withCredentials: true });
      toast.success(`Ticket marked as ${newStatus.replace('_', ' ')}`);
      // Refresh tickets list
      const ticketsRes = await axios.get(`${API}/backoffice/customer/${companyId}/tickets`, { withCredentials: true });
      setCustomerTickets(ticketsRes.data.tickets || []);
      // Update expanded ticket data
      const updatedTicket = (ticketsRes.data.tickets || []).find(t => t.ticket_id === ticketId);
      if (updatedTicket) setExpandedTicketData(prev => ({ ...prev, ...updatedTicket }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingTicketStatus(false);
    }
  };

  const assignTicket = async (ticketId, staffUserId, staffName) => {
    try {
      await axios.put(`${API}/backoffice/customer/${companyId}/tickets/${ticketId}/assign`, {
        assigned_to: staffUserId,
        assigned_to_name: staffName
      }, { withCredentials: true });
      toast.success(`Ticket assigned to ${staffName}`);
      // Refresh tickets list
      const ticketsRes = await axios.get(`${API}/backoffice/customer/${companyId}/tickets`, { withCredentials: true });
      setCustomerTickets(ticketsRes.data.tickets || []);
      const updatedTicket = (ticketsRes.data.tickets || []).find(t => t.ticket_id === ticketId);
      if (updatedTicket) setExpandedTicketData(prev => ({ ...prev, ...updatedTicket }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign ticket");
    }
  };

  const sendTicketReply = async (ticketId) => {
    if (!ticketReply.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/backoffice/customer/${companyId}/tickets/${ticketId}/reply`, {
        message: ticketReply
      }, { withCredentials: true });
      toast.success("Reply sent");
      setTicketReply("");
      // Refresh thread
      const res = await axios.get(`${API}/backoffice/customer/${companyId}/tickets/${ticketId}/thread`, { withCredentials: true });
      setTicketThread(res.data.messages || []);
      // Refresh tickets list for updated status
      const ticketsRes = await axios.get(`${API}/backoffice/customer/${companyId}/tickets`, { withCredentials: true });
      setCustomerTickets(ticketsRes.data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  // Fetch user sessions
  const fetchUserSessions = async (userId) => {
    setLoadingSessions(true);
    try {
      const response = await axios.get(`${API}/activity/admin/users/${userId}/sessions`, { withCredentials: true });
      setUserSessions(response.data.sessions || []);
    } catch (error) {
      toast.error("Failed to load sessions");
      setUserSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Terminate all sessions for a user
  const handleTerminateAllSessions = async (user) => {
    if (!window.confirm(`This will log ${user.email} out of all devices. Continue?`)) return;
    
    setTerminatingSession(true);
    try {
      const response = await axios.delete(`${API}/activity/admin/users/${user.user_id}/sessions`, { withCredentials: true });
      toast.success(response.data.message || `Terminated all sessions for ${user.email}`);
      setShowSessionsDialog(false);
      setUserSessions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to terminate sessions");
    } finally {
      setTerminatingSession(false);
    }
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    setProcessingCancel(true);
    try {
      const endpoint = cancelType === 'white_label' 
        ? `${API}/subscriptions/admin/customers/${companyId}/cancel-white-label`
        : `${API}/subscriptions/admin/customers/${companyId}/cancel-subscription`;
      
      const response = await axios.post(endpoint, {
        reason: cancelReason,
        immediate: cancelImmediate
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setShowCancelDialog(false);
      setCancelReason("");
      setCancelImmediate(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setProcessingCancel(false);
    }
  };

  // Delete layout
  const handleDeleteLayout = async () => {
    if (!selectedLayout) return;
    
    setDeletingLayout(true);
    try {
      await axios.delete(`${API}/admin/layouts/${selectedLayout.id}`, { withCredentials: true });
      toast.success(`Layout "${selectedLayout.name}" deleted successfully`);
      setShowDeleteLayoutDialog(false);
      setSelectedLayout(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete layout");
    } finally {
      setDeletingLayout(false);
    }
  };

  // Open user sessions dialog
  const openSessionsDialog = (user) => {
    setSelectedUser(user);
    setShowSessionsDialog(true);
    fetchUserSessions(user.user_id);
  };

  // Open cancel dialog
  const openCancelDialog = (type) => {
    setCancelType(type);
    setCancelReason("");
    setCancelImmediate(false);
    setShowCancelDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { company, subscription, users, layouts, events, monthly_usage, refunds, audit_logs, layout_event_map, layout_user_map } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/backoffice/customers')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Customers
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{company.name}</h1>
                  <p className="text-xs text-gray-500">{company.customer_id || company.company_id}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {/* Unlock login button — only visible when the owner is
                  currently locked. Calls /auth/admin/unlock-login and
                  refetches to clear the pill. */}
              {lockoutStatus?.locked && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200" data-testid="customer-lockout-pill">
                  <Lock className="w-4 h-4 text-red-600" />
                  <div className="text-xs">
                    <p className="font-semibold text-red-900">Login locked (stage {lockoutStatus.stage})</p>
                    <p className="text-red-600">{Math.ceil((lockoutStatus.seconds_remaining || 0) / 60)} min remaining</p>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={unlockingLogin}
                    data-testid="customer-unlock-login-btn"
                    onClick={async () => {
                      setUnlockingLogin(true);
                      try {
                        const r = await axios.post(
                          `${API}/auth/admin/unlock-login`,
                          { email: lockoutStatus.email },
                          { withCredentials: true }
                        );
                        toast.success(`Unlocked ${lockoutStatus.email} (${r.data?.cleared || 0} row(s) cleared)`);
                        setLockoutStatus({ locked: false, email: lockoutStatus.email });
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to unlock");
                      } finally {
                        setUnlockingLogin(false);
                      }
                    }}
                  >
                    {unlockingLogin ? "Unlocking…" : "Unlock login"}
                  </Button>
                </div>
              )}
              <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{users?.length || 0}</p>
                <p className="text-sm text-gray-500">Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Layout className="w-8 h-8 text-violet-600" />
              <div>
                <p className="text-2xl font-bold">{layouts?.length || 0}</p>
                <p className="text-sm text-gray-500">Layouts</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{events?.length || 0}</p>
                <p className="text-sm text-gray-500">Events</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold">{monthly_usage?.total_seats?.toLocaleString() || 0}</p>
                <p className="text-sm text-gray-500">Seats This Month</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Company Info & Subscription */}
          <div className="space-y-6">
            {/* Read-Only Mode Banner */}
            {viewMode === "readonly" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                <Lock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-amber-800 font-medium text-sm">Read-Only View</p>
                  <p className="text-amber-700 text-xs">You can view this customer's details but cannot make changes.</p>
                </div>
              </div>
            )}
            
            {/* Company Information */}
            <Card className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="w-5 h-5" /> Company Information
                </h2>
                {!editingCompany && canEdit && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditForm({
                      name: company?.name || "",
                      billing_email: company?.billing_email || "",
                      phone: company?.phone || "",
                      website: company?.website || "",
                      address: company?.address || "",
                      city: company?.city || "",
                      postcode: company?.postcode || "",
                      country: company?.country || "",
                      vat_number: company?.vat_number || "",
                      vat_registered: company?.vat_registered || false
                    });
                    setEditingCompany(true);
                  }}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
              </div>
              
              {editingCompany ? (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setSavingEdit(true);
                  try {
                    await axios.put(`${API}/subscriptions/admin/company/${companyId}`, editForm, { withCredentials: true });
                    toast.success("Company information updated");
                    setEditingCompany(false);
                    fetchData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to update company");
                  } finally {
                    setSavingEdit(false);
                  }
                }} className="space-y-4">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Company Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Website</Label>
                      <Input
                        value={editForm.website}
                        onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Phone</Label>
                      <PhoneInput
                        value={editForm.phone}
                        onChange={(phone) => setEditForm({...editForm, phone})}
                        autoDetectCountry={false}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Billing Email</Label>
                      <Input
                        type="email"
                        value={editForm.billing_email}
                        onChange={(e) => setEditForm({...editForm, billing_email: e.target.value})}
                        placeholder="billing@example.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Address</Label>
                      <Input
                        value={editForm.address}
                        onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                        placeholder="123 Business Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">City</Label>
                        <Input
                          value={editForm.city}
                          onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                          placeholder="London"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Postcode</Label>
                        <Input
                          value={editForm.postcode}
                          onChange={(e) => setEditForm({...editForm, postcode: e.target.value})}
                          placeholder="SW1A 1AA"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Country</Label>
                      <Input
                        value={editForm.country}
                        onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                        placeholder="United Kingdom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">VAT Number</Label>
                      <Input
                        value={editForm.vat_number}
                        onChange={(e) => setEditForm({...editForm, vat_number: e.target.value})}
                        placeholder="GB123456789"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editForm.vat_registered}
                        onCheckedChange={(checked) => setEditForm({...editForm, vat_registered: checked})}
                      />
                      <Label className="text-sm">VAT Registered</Label>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" size="sm" disabled={savingEdit}>
                      {savingEdit ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditingCompany(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Company Name</p>
                      <p className="font-medium">{company.name || <span className="text-gray-400">Not set</span>}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-start gap-3">
                      <Hash className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <p className="text-xs text-gray-500">Company ID</p>
                        <p className="font-medium font-mono text-xs">{company.company_id}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Hash className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <p className="text-xs text-gray-500">Customer ID</p>
                        <p className="font-medium font-mono text-xs">{company.customer_id || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Globe className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Website</p>
                      {company.website ? (
                        <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="font-medium text-violet-600 hover:text-violet-700 hover:underline">
                          {company.website}
                        </a>
                      ) : (
                        <p className="font-medium text-gray-400">Not set</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      {company.phone ? (
                        <a href={`tel:${company.phone}`} className="font-medium text-green-600 hover:text-green-700 hover:underline">
                          {company.phone}
                        </a>
                      ) : (
                        <p className="font-medium text-gray-400">Not set</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Billing Email</p>
                      {company.billing_email ? (
                        <a href={`mailto:${company.billing_email}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                          {company.billing_email}
                        </a>
                      ) : (
                        <p className="font-medium text-gray-400">Not set</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="font-medium">
                        {company.address ? `${company.address}, ${company.city || ''} ${company.postcode || ''} ${company.country || ''}` : <span className="text-gray-400">Not set</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">VAT Number</p>
                      <p className="font-medium">{company.vat_number || <span className="text-gray-400">Not registered</span>}</p>
                      {company.vat_registered && <span className="text-xs text-green-600">VAT Registered</span>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="font-medium">{formatDate(company.created_at)}</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* API Keys */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Key className="w-5 h-5" /> API Keys
                {data?.api_key_limits && (
                  <span className="text-xs font-normal text-gray-400 ml-auto">
                    {data.api_key_limits.active_keys}/{data.api_key_limits.max_keys} active
                    {data.api_key_limits.custom_limit && <span className="text-violet-500 ml-1">(custom)</span>}
                    {data.api_key_limits.is_blocked && <span className="text-red-500 ml-1 font-medium">(BLOCKED)</span>}
                  </span>
                )}
              </h2>

              {/* Block/Unblock toggle */}
              {data?.can_edit && (
                <div className={`mb-3 p-2.5 rounded-lg flex items-center justify-between ${data?.api_key_limits?.is_blocked ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div>
                    <p className="text-xs font-medium text-gray-700">
                      {data?.api_key_limits?.is_blocked ? 'API key creation is BLOCKED' : 'API key creation allowed'}
                    </p>
                    {data?.api_key_limits?.blocked_reason && (
                      <p className="text-xs text-red-500">{data.api_key_limits.blocked_reason}</p>
                    )}
                  </div>
                  <Button size="sm" variant={data?.api_key_limits?.is_blocked ? "default" : "outline"} className={`text-xs ${data?.api_key_limits?.is_blocked ? 'bg-green-600 hover:bg-green-700' : 'text-red-600 hover:bg-red-50'}`} onClick={async () => {
                    const isBlocked = data?.api_key_limits?.is_blocked;
                    const reason = isBlocked ? null : window.prompt('Reason for blocking (optional):');
                    try {
                      await axios.put(`${API}/backoffice/customer/${company.company_id}/block-api-keys`, { blocked: !isBlocked, reason }, { withCredentials: true });
                      toast.success(isBlocked ? 'API keys unblocked' : 'API keys blocked');
                      fetchData();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }}>
                    {data?.api_key_limits?.is_blocked ? 'Unblock' : 'Block'}
                  </Button>
                </div>
              )}

              {data?.api_keys && data.api_keys.length > 0 ? (
                <div className="space-y-2">
                  {/* Revoke All button */}
                  {data?.can_edit && data.api_key_limits?.active_keys > 0 && (
                    <div className="flex justify-end mb-1">
                      <Button size="sm" variant="ghost" className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={async () => {
                        if (!window.confirm(`Revoke ALL ${data.api_key_limits.active_keys} active API keys? All integrations will stop working.`)) return;
                        try {
                          await axios.delete(`${API}/backoffice/customer/${company.company_id}/api-keys`, { withCredentials: true });
                          toast.success('All API keys revoked');
                          fetchData();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}>
                        Revoke All Keys
                      </Button>
                    </div>
                  )}
                  {data.api_keys.filter(k => k.active).map((k, i) => (
                    <div key={k.key_id || i} className="p-3 border rounded-lg group">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded bg-green-50 flex items-center justify-center flex-shrink-0">
                          <Key className="w-3.5 h-3.5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{k.name}</span>
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Active</span>
                          </div>
                          <p className="text-xs text-gray-400">
                            Created {k.created_at ? new Date(k.created_at).toLocaleDateString() : ''}
                            {k.created_by ? ` by ${k.created_by}` : ''}
                          </p>
                        </div>
                        {data?.can_edit && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={async () => {
                              if (!window.confirm(`Regenerate "${k.name}"? The old key will stop working immediately.`)) return;
                              try {
                                const res = await axios.post(`${API}/backoffice/customer/${company.company_id}/regenerate-api-key`, { key_id: k.key_id || k.key_full }, { withCredentials: true });
                                toast.success(`Key regenerated: ${res.data.api_key.substring(0, 12)}...`);
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                            }}>
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs text-red-500 hover:bg-red-50" onClick={async () => {
                              if (!window.confirm(`Revoke "${k.name}"?`)) return;
                              try {
                                await axios.delete(`${API}/backoffice/customer/${company.company_id}/api-keys/${k.key_id || k.key_full}`, { withCredentials: true });
                                toast.success(`Key "${k.name}" revoked`);
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                        <code className="text-xs font-mono text-gray-600 flex-1 truncate select-all">
                          {showApiKey === (k.key_id || i) ? k.key_full : k.key_preview}
                        </code>
                        <button onClick={() => setShowApiKey(showApiKey === (k.key_id || i) ? null : (k.key_id || i))} className="text-gray-400 hover:text-gray-600">
                          {showApiKey === (k.key_id || i) ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => { navigator.clipboard.writeText(k.key_full); toast.success('Key copied'); }} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {data.api_keys.filter(k => !k.active).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer">{data.api_keys.filter(k => !k.active).length} revoked</summary>
                      {data.api_keys.filter(k => !k.active).map((k, i) => (
                        <div key={k.key_id || i} className="flex items-center gap-2 p-1.5 opacity-50 mt-1">
                          <Key className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500 line-through">{k.name}</span>
                          <code className="text-xs text-gray-400">{k.key_preview}</code>
                        </div>
                      ))}
                    </details>
                  )}
                  {/* Set custom limit */}
                  {data?.can_edit && (
                    <div className="mt-3 pt-3 border-t">
                      <label className="text-xs text-gray-500 font-medium">Custom Key Limit</label>
                      <div className="flex gap-2 mt-1">
                        <input type="number" min="1" max="50" defaultValue={data.api_key_limits?.custom_limit || data.api_key_limits?.plan_default || 3} id="custom-key-limit" className="w-20 px-2 py-1 text-sm border rounded" />
                        <Button size="sm" variant="outline" onClick={async () => {
                          const val = parseInt(document.getElementById('custom-key-limit').value);
                          if (!val || val < 1 || val > 50) { toast.error('Enter 1-50'); return; }
                          try {
                            await axios.put(`${API}/backoffice/customer/${company.company_id}/api-key-limit`, { api_key_limit: val }, { withCredentials: true });
                            toast.success(`Limit set to ${val}`);
                            fetchData();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                        }}>Set</Button>
                        {data.api_key_limits?.custom_limit && (
                          <Button size="sm" variant="ghost" className="text-gray-400 text-xs" onClick={async () => {
                            try {
                              await axios.delete(`${API}/backoffice/customer/${company.company_id}/api-key-limit`, { withCredentials: true });
                              toast.success('Reset to plan default');
                              fetchData();
                            } catch (e) { toast.error('Failed'); }
                          }}>Reset</Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No API keys generated</p>
              )}
            </Card>

            {/* Support Tickets & Enquiries */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Support Tickets & Enquiries
                {customerTickets.filter(t => t.subject?.toLowerCase().includes('enterprise')).length > 0 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium animate-pulse">
                    Enterprise Enquiry
                  </span>
                )}
              </h2>
              {customerTickets.length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {customerTickets.map((ticket) => (
                    <div key={ticket.ticket_id} className={`rounded-lg border ${
                      ticket.subject?.toLowerCase().includes('enterprise') 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div 
                        className="p-3 cursor-pointer hover:bg-gray-100/50 transition-colors"
                        onClick={() => fetchTicketThread(ticket.ticket_id)}
                        data-testid={`ticket-row-${ticket.ticket_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm truncate">{ticket.subject}</span>
                              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                                ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                                ticket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{ticket.status}</span>
                              {ticket.priority === 'high' && (
                                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">High Priority</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              #{ticket.ticket_number} &middot; {ticket.category} &middot; {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expandedTicketId === ticket.ticket_id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Inline ticket thread */}
                      {expandedTicketId === ticket.ticket_id && (
                        <div className="border-t px-3 pb-3" data-testid={`ticket-thread-${ticket.ticket_id}`}>
                          {loadingThread ? (
                            <p className="text-xs text-gray-400 py-3 text-center">Loading thread...</p>
                          ) : (
                            <>
                              {/* Ticket Actions Bar */}
                              <div className="flex flex-wrap items-center gap-2 py-2 border-b">
                                {/* Status Buttons */}
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500 mr-1">Status:</span>
                                  {["open", "in_progress", "resolved", "closed"].map((st) => (
                                    <button
                                      key={st}
                                      disabled={updatingTicketStatus || ticket.status === st}
                                      onClick={() => updateTicketStatus(ticket.ticket_id, st)}
                                      data-testid={`ticket-status-${st}-${ticket.ticket_id}`}
                                      className={`px-2 py-0.5 text-[10px] rounded font-medium transition-colors ${
                                        ticket.status === st 
                                          ? st === 'open' ? 'bg-blue-200 text-blue-800 ring-1 ring-blue-400'
                                            : st === 'in_progress' ? 'bg-amber-200 text-amber-800 ring-1 ring-amber-400'
                                            : st === 'resolved' ? 'bg-green-200 text-green-800 ring-1 ring-green-400'
                                            : 'bg-gray-200 text-gray-800 ring-1 ring-gray-400'
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer'
                                      }`}
                                    >
                                      {st.replace('_', ' ')}
                                    </button>
                                  ))}
                                </div>
                                
                                {/* Assign dropdown */}
                                <div className="flex items-center gap-1 ml-auto">
                                  <span className="text-[10px] text-gray-500">Assign:</span>
                                  <select
                                    className="text-xs border rounded px-1.5 py-0.5 bg-white max-w-[140px]"
                                    data-testid={`ticket-assign-${ticket.ticket_id}`}
                                    value={expandedTicketData?.assigned_to || ticket.assigned_to || ""}
                                    onChange={(e) => {
                                      const sel = staffList.find(s => s.user_id === e.target.value);
                                      if (sel) assignTicket(ticket.ticket_id, sel.user_id, sel.name || sel.email);
                                    }}
                                  >
                                    <option value="">Unassigned</option>
                                    {staffList.map((s) => (
                                      <option key={s.user_id} value={s.user_id}>{s.name || s.email}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* Messages */}
                              <div className="max-h-[250px] overflow-y-auto space-y-2 py-2">
                                {ticketThread.length === 0 ? (
                                  <p className="text-xs text-gray-400 text-center py-2">No messages yet</p>
                                ) : (
                                  ticketThread.map((msg) => (
                                    <div 
                                      key={msg.message_id}
                                      className={`p-2 rounded text-sm ${
                                        msg.sender_type === 'staff' 
                                          ? 'bg-blue-50 border-l-2 border-blue-400 ml-4' 
                                          : 'bg-white border-l-2 border-gray-300 mr-4'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-xs">
                                          {msg.sender_type === 'staff' ? `${msg.sender_name || 'Staff'}` : `${msg.sender_name || 'Customer'}`}
                                        </span>
                                        <span className="text-[10px] text-gray-400">
                                          {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                      {msg.is_internal && (
                                        <span className="text-[10px] text-amber-600 mt-1 block">Internal note</span>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                              
                              {/* Reply input */}
                              <div className="flex gap-2 mt-2 pt-2 border-t">
                                <Textarea
                                  placeholder="Type a reply..."
                                  value={ticketReply}
                                  onChange={(e) => setTicketReply(e.target.value)}
                                  className="text-sm min-h-[60px] resize-none flex-1"
                                  data-testid={`ticket-reply-input-${ticket.ticket_id}`}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                      sendTicketReply(ticket.ticket_id);
                                    }
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  className="self-end"
                                  disabled={!ticketReply.trim() || sendingReply}
                                  onClick={() => sendTicketReply(ticket.ticket_id)}
                                  data-testid={`ticket-send-reply-${ticket.ticket_id}`}
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No support tickets</p>
              )}
            </Card>

            {/* Subscription */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Subscription
              </h2>
              {subscription ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Plan</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{subscription.plan_id}</span>
                      {subscription.is_custom && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Custom
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      subscription.status === 'active' ? 'bg-green-100 text-green-700' :
                      subscription.status === 'trial' ? 'bg-blue-100 text-blue-700' :
                      subscription.status === 'cancelling' ? 'bg-amber-100 text-amber-700' :
                      subscription.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {subscription.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Monthly Price</span>
                    <span className="font-medium">{formatCurrency(subscription.monthly_price)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Seat Allowance</span>
                    <span className="font-medium">
                      {subscription.monthly_seat_allowance === -1 ? 'Unlimited' : subscription.monthly_seat_allowance?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Overage Rate</span>
                    <span className="font-medium">{formatCurrency(subscription.overage_per_seat)}/seat</span>
                  </div>
                  {subscription.expires_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Expires</span>
                      <span className="font-medium">{formatDate(subscription.expires_at)}</span>
                    </div>
                  )}
                  {subscription.scheduled_cancel_at && (
                    <div className="flex items-center justify-between text-amber-600">
                      <span>Cancelling On</span>
                      <span className="font-medium">{formatDate(subscription.scheduled_cancel_at)}</span>
                    </div>
                  )}
                  
                  {/* Admin Actions */}
                  {subscription.status !== 'cancelled' && data?.can_edit && (
                    <div className="pt-3 border-t space-y-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        data-testid="change-plan-btn"
                        onClick={openChangePlanDialog}
                      >
                        Change Plan
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openCancelDialog('subscription')}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                  {(subscription.status === 'cancelled' || subscription.status === 'cancelling') && data?.can_edit && (
                    <div className="pt-3 border-t space-y-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid="reactivate-subscription-btn"
                        onClick={async () => {
                          if (!window.confirm(`Reactivate subscription for ${company?.name}?`)) return;
                          try {
                            const res = await axios.post(`${API}/subscriptions/admin/customers/${companyId}/reactivate-subscription`, {}, { withCredentials: true });
                            toast.success(res.data.message);
                            fetchData();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to reactivate');
                          }
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reactivate Subscription
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No active subscription</p>
              )}
            </Card>

            {/* White Label Subscription */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5" /> White Label
              </h2>
              {company.white_label ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      company.white_label_status === 'active' ? 'bg-green-100 text-green-700' :
                      company.white_label_status === 'cancelling' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {company.white_label_status || 'Active'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Monthly Price</span>
                    <span className="font-medium">£20.00/mo</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Subscribed On</span>
                    <span className="font-medium">{formatDate(company.white_label_subscribed_at)}</span>
                  </div>
                  {company.white_label_cancel_at && (
                    <div className="flex items-center justify-between text-amber-600">
                      <span>Cancelling On</span>
                      <span className="font-medium">{formatDate(company.white_label_cancel_at)}</span>
                    </div>
                  )}
                  
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-purple-700">&quot;Powered by SeatPilot&quot; badge is hidden</span>
                    </div>
                  </div>
                  
                  {/* Cancel White Label Button */}
                  {company.white_label_status !== 'cancelled' && !company.white_label_cancelling && (
                    <div className="pt-3 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openCancelDialog('white_label')}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel White Label
                      </Button>
                    </div>
                  )}
                  {/* Reactivate White Label Button */}
                  {(company.white_label_status === 'cancelled' || company.white_label_cancelling) && (
                    <div className="pt-3 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid="reactivate-white-label-btn"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/subscriptions/admin/customers/${companyId}/reactivate-white-label`);
                            toast.success('White Label reactivated');
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to reactivate');
                          }
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reactivate White Label
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Palette className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No white-label subscription</p>
                  <p className="text-xs text-gray-400 mt-1">&quot;Powered by SeatPilot&quot; badge is visible</p>
                </div>
              )}
            </Card>

            {/* Refunds */}
            {refunds && refunds.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Refund History</h2>
                <div className="space-y-3">
                  {refunds.map(refund => (
                    <div key={refund.refund_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{formatCurrency(refund.amount)}</p>
                        <p className="text-xs text-gray-500">{refund.reason}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        refund.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {refund.status}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Middle Column - Customer & Team */}
          <div className="space-y-6">
            {/* Owner/Customer Section */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" /> Customer (Owner)
              </h2>
              {users && users.length > 0 ? (
                <>
                  {users.filter(u => u.role === 'owner' || u.role === 'admin').slice(0, 1).map(owner => (
                    <div 
                      key={owner.user_id} 
                      className={`p-4 rounded-lg border ${owner.is_blocked ? 'bg-red-50 border-red-200' : 'bg-violet-50 border-violet-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${owner.is_blocked ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-600'}`}>
                            {owner.name?.charAt(0) || owner.email?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold flex items-center gap-2 text-lg">
                              {owner.name || 'No name'}
                              {owner.is_blocked && <Ban className="w-4 h-4 text-red-500" />}
                            </p>
                            <p className="text-sm text-gray-500">{owner.email}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-medium">Owner</span>
                              {owner.email_verified ? (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Verified
                                </span>
                              ) : (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Unverified</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Owner Last Login Info */}
                      {owner.last_login_at && (
                        <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Last login: {formatDate(owner.last_login_at)}
                          </span>
                          {owner.last_login_location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {owner.last_login_location}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => { setSelectedUser(owner); setShowUserDetails(true); }}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View Details
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openSessionsDialog(owner)}
                          title="Manage Sessions"
                        >
                          <LogOut className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleSendPasswordReset(owner)} title="Send Password Reset">
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={owner.is_blocked ? "text-green-600" : "text-red-600"}
                              onClick={() => handleBlockUser(owner, !owner.is_blocked)}
                              title={owner.is_blocked ? "Unblock" : "Block"}
                            >
                              {owner.is_blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-gray-500 text-sm">No owner found</p>
              )}
            </Card>

            {/* Team Members Section */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Team Members ({Math.max(0, (users?.length || 0) - 1)})
              </h2>
              {users && users.filter(u => u.role !== 'owner').length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {users.filter(u => u.role !== 'owner' && !(u.role === 'admin' && users.filter(x => x.role === 'owner').length === 0)).map(member => (
                    <div 
                      key={member.user_id} 
                      className={`p-4 rounded-lg border ${member.is_blocked ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.is_blocked ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                            {member.name?.charAt(0) || member.email?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {member.name || 'No name'}
                              {member.is_blocked && <Ban className="w-4 h-4 text-red-500" />}
                            </p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                member.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                member.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                                member.role === 'editor' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{member.role}</span>
                              {member.email_verified ? (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Verified
                                </span>
                              ) : (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Unverified</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Member Last Login Info */}
                      {member.last_login_at && (
                        <div className="mt-2 text-xs text-gray-400 flex items-center gap-3">
                          <span>Last login: {formatDate(member.last_login_at)}</span>
                          {member.last_login_location && <span>• {member.last_login_location}</span>}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => { setSelectedUser(member); setShowUserDetails(true); }}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openSessionsDialog(member)}
                          title="Sessions"
                        >
                          <LogOut className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleSendPasswordReset(member)} title="Reset Password">
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={member.is_blocked ? "text-green-600" : "text-red-600"}
                              onClick={() => handleBlockUser(member, !member.is_blocked)}
                            >
                              {member.is_blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No team members added yet</p>
                  <p className="text-xs text-gray-400 mt-1">Customer can invite team members from their dashboard</p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Layouts & Events */}
          <div className="space-y-6">
            {/* Layouts */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Layout className="w-5 h-5" /> Layouts ({layouts?.length || 0})
              </h2>
              {layouts && layouts.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {layouts.map(layout => {
                    const linkedEvent = layout_event_map?.[layout.id];
                    const createdBy = layout_user_map?.[layout.id];
                    
                    return (
                      <div key={layout.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-violet-300 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{layout.name}</p>
                            <p className="text-xs text-gray-500">{layout.venue_name || 'No venue'}</p>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {linkedEvent && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Linked</span>
                            )}
                          </div>
                        </div>
                        {createdBy && (
                          <p className="text-xs text-gray-400 mt-2">Created by: {createdBy.name || createdBy.email}</p>
                        )}
                        {linkedEvent && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-gray-500">Event: {linkedEvent.name || linkedEvent.event_id}</p>
                          </div>
                        )}
                        
                        {/* Layout Actions */}
                        <div className="mt-3 pt-2 border-t flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 text-xs"
                            onClick={() => window.open(`/builder/${layout.id}`, '_blank')}
                          >
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 text-xs"
                            onClick={() => window.open(`/builder/${layout.id}`, '_blank')}
                          >
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 hover:text-red-700 text-xs"
                            onClick={() => { setSelectedLayout(layout); setShowDeleteLayoutDialog(true); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No layouts</p>
              )}
            </Card>

            {/* Events */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Events ({events?.length || 0})
              </h2>
              {events && events.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {events.map(event => (
                    <div key={event.event_id || event.id} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium">{event.name || event.event_id}</p>
                      <p className="text-xs text-gray-500">{formatDate(event.date || event.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No events</p>
              )}
            </Card>

            {/* Payment Audit Log — chargeback-defence trail with IP, T&C
                consent, actor, timestamp. Always rendered (even when empty)
                so support can confirm at a glance there's nothing to show. */}
            <Card className="p-6" data-testid="customer-profile-audit">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold">Payment Audit Log</h2>
                  <p className="text-xs text-gray-500">
                    Every subscription/payment action with IP, timestamp and T&amp;C consent — used to defend chargebacks and team-access disputes.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="customer-profile-evidence-download"
                  disabled={downloadingEvidence}
                  onClick={async () => {
                    setDownloadingEvidence(true);
                    try {
                      const r = await axios.get(
                        `${API}/subscriptions/super-admin/companies/${companyId}/evidence-pack`,
                        { withCredentials: true, responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `evidence-pack-${companyId}-${new Date().toISOString().slice(0, 10)}.html`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Evidence pack downloaded");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to generate evidence pack");
                    } finally {
                      setDownloadingEvidence(false);
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {downloadingEvidence ? "Generating…" : "Download evidence pack"}
                </Button>
              </div>
              {paymentAudit.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No payment events recorded yet.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {paymentAudit.map((row) => {
                    const cls = row.action.includes('cancel') || row.action.includes('disabled')
                      ? 'bg-red-100 text-red-700'
                      : (row.action.includes('enabled') || row.action.includes('created'))
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-violet-100 text-violet-700';
                    return (
                      <div key={row.audit_id} className="flex items-start gap-3 p-2 rounded border border-gray-100 bg-gray-50 text-sm" data-testid={`profile-audit-row-${row.audit_id}`}>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>{row.action}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{row.actor_email || row.actor_user_id || 'system'}{row.is_super_admin ? ' (super-admin)' : (row.actor_role ? ` (${row.actor_role})` : '')}</p>
                          <p className="text-xs text-gray-600">
                            {formatDate(row.created_at)} • IP <span className="font-mono">{row.ip || '—'}</span>{row.country ? ` (${row.country})` : ''}
                            {row.terms_accepted === true && <span className="ml-2 text-emerald-600 font-semibold">✓ T&amp;C accepted</span>}
                          </p>
                          {row.details?.plan_name && (
                            <p className="text-[11px] text-gray-500 mt-0.5">Plan: {row.details.plan_name}{row.details?.stripe_subscription_id ? ` • Stripe: ${row.details.stripe_subscription_id}` : ''}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Audit Logs */}
            {audit_logs && audit_logs.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {audit_logs.slice(0, 10).map(log => (
                    <div key={log.log_id} className="text-sm py-2 border-b last:border-0">
                      <p className="font-medium">{log.action?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-500">{formatDate(log.created_at)}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* User Details Dialog */}
      <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${selectedUser.is_blocked ? 'bg-red-100' : 'bg-violet-100'}`}>
                  <span className={`text-2xl ${selectedUser.is_blocked ? 'text-red-600' : 'text-violet-600'}`}>
                    {selectedUser.name?.charAt(0) || selectedUser.email?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-xl font-semibold">{selectedUser.name || 'No name'}</p>
                  <p className="text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">User ID</p>
                  <p className="font-mono text-xs">{selectedUser.user_id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Role</p>
                  <p className="font-medium">{selectedUser.role}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedUser.mobile || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Country</p>
                  <p className="font-medium">{selectedUser.country || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email Verified</p>
                  <p className={`font-medium ${selectedUser.email_verified ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedUser.email_verified ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className={`font-medium ${selectedUser.is_blocked ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedUser.is_blocked ? 'Blocked' : 'Active'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Last Login</p>
                  <p className="font-medium">{formatDate(selectedUser.last_login)}</p>
                </div>
              </div>
              
              {selectedUser.login_history && selectedUser.login_history.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Login History</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedUser.login_history.slice(0, 5).map((login, i) => (
                      <div key={i} className="text-xs flex justify-between bg-gray-50 p-2 rounded">
                        <span>{formatDate(login.timestamp)}</span>
                        <span className="text-gray-400">{login.ip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sessions Management Dialog */}
      <Dialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5" /> Active Sessions
            </DialogTitle>
            <DialogDescription>
              Manage active sessions for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {loadingSessions ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading sessions...</p>
            </div>
          ) : userSessions.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {userSessions.map(session => (
                <div key={session.session_id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{session.device?.browser || 'Unknown Browser'}</p>
                      <p className="text-xs text-gray-500">{session.device?.os || 'Unknown OS'}</p>
                      <p className="text-xs text-gray-400 mt-1">Last active: {formatDate(session.last_active || session.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <LogOut className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No active sessions</p>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSessionsDialog(false)}>Close</Button>
            {userSessions.length > 0 && (
              <Button 
                variant="destructive"
                onClick={() => handleTerminateAllSessions(selectedUser)}
                disabled={terminatingSession}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {terminatingSession ? "Logging out..." : "Log Out All Devices"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> 
              Cancel {cancelType === 'white_label' ? 'White Label' : 'Subscription'}
            </DialogTitle>
            <DialogDescription>
              This will cancel the {cancelType === 'white_label' ? 'white-label add-on' : 'subscription'} for {company?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason for cancellation (optional)</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Customer requested cancellation..."
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <p className="font-medium text-amber-800">Cancel immediately?</p>
                <p className="text-xs text-amber-600">
                  {cancelImmediate 
                    ? "Access will be revoked immediately" 
                    : "Access continues until end of billing period"}
                </p>
              </div>
              <Switch
                checked={cancelImmediate}
                onCheckedChange={setCancelImmediate}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Keep Active</Button>
            <Button 
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={processingCancel}
            >
              {processingCancel ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Layout Dialog */}
      <Dialog open={showDeleteLayoutDialog} onOpenChange={setShowDeleteLayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Delete Layout
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedLayout?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <p className="font-medium">Warning:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>All seat configurations will be lost</li>
                <li>Linked events will be unlinked</li>
                <li>This cannot be undone</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteLayoutDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteLayout}
              disabled={deletingLayout}
            >
              {deletingLayout ? "Deleting..." : "Delete Layout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Change Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Select a new plan for {company?.name}. Current plan: <strong>{data?.subscription?.plan_id}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="mb-2 block">Select Plan</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger data-testid="change-plan-select" className="w-full">
                  <SelectValue placeholder="Choose a plan..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(availablePlans).map(([planId, plan]) => (
                    <SelectItem key={planId} value={planId} data-testid={`plan-option-${planId}`}>
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{plan.name}</span>
                        <span className="text-xs text-gray-400">
                          {plan.price === null || plan.price === undefined ? 'Custom' : `${formatCurrency(plan.price)}`}
                          {plan.billing_period === 'yearly' ? '/yr' : plan.price !== null && plan.price !== undefined ? '/mo' : ''}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPlanId && availablePlans[selectedPlanId] && (
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg text-sm">
                <p className="font-medium text-violet-800">{availablePlans[selectedPlanId].name}</p>
                <p className="text-xs text-violet-600 mt-1">{availablePlans[selectedPlanId].description}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlanDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleChangePlan}
              disabled={changingPlan || !selectedPlanId}
              data-testid="confirm-change-plan-btn"
            >
              {changingPlan ? "Changing..." : "Change Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
